// The Changes tab of the right dock (spec/dashboard.md section 4.5): "Unsaved changes (N)", Save All / Discard All, the
// last save's outcome, then groups by actor → template → entity with one line per coalesced field change. Above the groups
// the commit message of the next Save All (section 4.3 step 2): what the user types, or the automatic summary shown as the
// placeholder (../workspace/summary.ts).
import { AlertTriangle, CornerDownRight, X } from './icons';
import { Fragment, useMemo } from 'react';
import { summarizeDrafts } from '../workspace/summary';
import type { Draft, EntityKey, Workspace } from '../workspace/Workspace';
import { useWorkspaceVersion } from '../workspace/react';
import { Button } from './Button';
import { Badge, draftTone, draftWord } from './Chip';
import { formatValue } from './DiffView';
import { formatInstantWithZone } from './time';

export interface ChangesPanelProps {
  workspace: Workspace;
  onOpen: (draft: Draft) => void;
  onSaveAll: () => void;
  onDryRun?: () => void;
  onDiscardAll: () => void;
  saving?: boolean;
  canWrite: boolean;
  /** the permission Save All needs here (content_write on a branch; manage_environment / players_write on an environment page) */
  writePermission?: string;
}

function displayPath(path: string): string {
  if (path === '' || path === '$') return '(whole value)';
  return path.startsWith('data.') ? path.slice(5) : path;
}

export function ChangesPanel({ workspace, onOpen, onSaveAll, onDryRun, onDiscardAll, saving, canWrite, writePermission = 'content_write' }: ChangesPanelProps) {
  useWorkspaceVersion(workspace);
  const drafts = workspace.all;
  const count = workspace.changeCount;
  const groups = useMemo(() => {
    const byActor = new Map<string, Map<string, Draft[]>>();
    for (const actor of workspace.actors) {
      const byTemplate = new Map<string, Draft[]>();
      for (const d of workspace.draftsOf(actor)) {
        const t = d.template ?? d.kind;
        const list = byTemplate.get(t) ?? [];
        list.push(d);
        byTemplate.set(t, list);
      }
      byActor.set(actor, byTemplate);
    }
    return byActor;
  }, [drafts, workspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const problems = drafts.filter((d) => d.error || d.conflict);
  const last = workspace.lastSave;

  return (
    <div className="changes" aria-label="Unsaved changes">
      <div className="row" style={{ marginBottom: 8 }}>
        <strong>Unsaved changes ({count})</strong>
        <span className="spacer" />
        {onDryRun && (
          <Button size="s" variant="ghost" onClick={onDryRun} disabled={count === 0 || saving}>
            Dry run
          </Button>
        )}
        <Button size="s" variant="danger" onClick={onDiscardAll} disabled={count === 0 || saving}>
          Discard all
        </Button>
        <Button size="s" variant="primary" onClick={onSaveAll} disabled={count === 0 || workspace.isProtected} missingPermission={canWrite ? null : writePermission} busy={saving} title={workspace.isProtected ? 'This branch is protected: changes land through merge requests.' : 'Save all (Ctrl+S)'}>
          Save all
        </Button>
      </div>
      {last && (
        <div className={`banner small ${last.failed || last.blocked ? 'banner-warning' : 'banner-success'}`} style={{ marginBottom: 8 }} role="status">
          <div className="banner-body">
            {last.dryRun ? 'Dry run' : 'Last save'} at {formatInstantWithZone(Math.floor(last.at / 1000))}: {last.saved} {last.dryRun ? 'valid' : 'saved'}
            {last.failed ? `, ${last.failed} refused` : ''}
            {last.blocked ? `, ${last.blocked} blocked by validators` : ''}
            {problems.length > 0 ? ' — nothing that failed left the workspace.' : ''}
          </div>
        </div>
      )}
      {count > 0 && (
        <div className="changes-message">
          <label className="field-label" htmlFor="changes-save-message">
            Save message
          </label>
          <input
            id="changes-save-message"
            className="input input-s"
            type="text"
            value={workspace.saveMessage}
            placeholder={summarizeDrafts(workspace)}
            onChange={(e) => workspace.setSaveMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (!saving && !workspace.isProtected && canWrite) onSaveAll();
              }
            }}
          />
        </div>
      )}
      {count === 0 && <p className="muted">No unsaved changes.</p>}
      {Array.from(groups.entries()).map(([actor, byTemplate]) => (
        <div key={actor} className="changes-group">
          {groups.size > 1 || actor !== 'user' ? <h3>{actor === 'user' ? 'Your changes' : actor.replace('extension:', 'Extension ')}</h3> : null}
          {Array.from(byTemplate.entries()).map(([template, list]) => (
            <Fragment key={template}>
              <div className="muted small" style={{ margin: '6px 0 2px' }}>
                {template}
              </div>
              {list.map((d) => (
                <div key={d.key} className={`changes-entity ${d.error ? 'has-error' : ''} ${d.conflict ? 'has-conflict' : ''}`} data-testid={`change-${d.key}`}>
                  <div className="changes-entity-head">
                    <span className="label" title={d.id}>
                      {d.label}
                    </span>
                    <Badge tone={draftTone[d.status]}>{draftWord[d.status]}</Badge>
                    {d.force && (
                      <Badge tone="danger" title="The next Save All sends this edit with force: true (the player was online)">
                        forced
                      </Badge>
                    )}
                    {d.remote && !d.conflict && (
                      <Badge tone="warning" title={`Changed by ${d.remote.by ?? 'someone'} at ${d.remote.at ?? '?'}`}>
                        changed remotely
                      </Badge>
                    )}
                    <button type="button" className="icon-button" aria-label={`Open ${d.label}`} onClick={() => onOpen(d)}>
                      <CornerDownRight aria-hidden="true" />
                    </button>
                    <button type="button" className="icon-button" aria-label={`Discard all changes of ${d.label}`} onClick={() => workspace.discardEntity(d.key)}>
                      <X aria-hidden="true" />
                    </button>
                  </div>
                  {d.status !== 'deleted' &&
                    d.changes.map((c) => (
                      <div key={c.path} className="change-line">
                        <span className="path">{displayPath(c.path)}:</span>
                        <span className="values">
                          {d.status !== 'new' && <span className="before">{formatValue(c.before)}</span>} <span className="after">{formatValue(c.after)}</span>
                        </span>
                        <button type="button" className="icon-button" aria-label={`Discard change to ${displayPath(c.path)} of ${d.label}`} onClick={() => workspace.discardChange(d.key, c.path)}>
                          <X aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  {d.warnings && d.warnings.length > 0 && (
                    <div className="changes-warnings" data-testid={`warnings-${d.key}`}>
                      {d.warnings.map((w, i) => (
                        <div key={i} className="warning small">
                          <AlertTriangle className="icon-s" aria-hidden="true" /> {w}
                        </div>
                      ))}
                    </div>
                  )}
                  {d.error && (
                    <div className="change-reason" role="alert">
                      {d.error.source === 'extension' ? 'Blocked by ' + (d.error.reasons ?? []).map((r) => r.extension).join(', ') + ': ' : ''}
                      {d.error.message}
                      {d.error.details?.map((x, i) => (
                        <div key={i}>
                          {x.field ? `${x.field}: ` : ''}
                          {x.message}
                        </div>
                      ))}
                    </div>
                  )}
                  {d.conflict?.online && (
                    <div className="change-reason" style={{ color: 'var(--hue-warning)' }} role="alert" data-testid={`online-${d.key}`}>
                      The player is online: {d.conflict.message} A forced edit can be overwritten by the device's next save.
                      <div className="row" style={{ marginTop: 4 }}>
                        <Button size="s" variant="danger" onClick={() => workspace.setForce(d.key, true)}>
                          Save anyway
                        </Button>
                        <Button size="s" variant="ghost" onClick={() => workspace.discardEntity(d.key)}>
                          Discard mine
                        </Button>
                      </div>
                    </div>
                  )}
                  {d.conflict && !d.conflict.online && (
                    <div className="change-reason" style={{ color: 'var(--hue-warning)' }} role="alert">
                      Someone else saved this {d.kind} since you started{d.conflict.by ? ` (${d.conflict.by}` : ''}
                      {d.conflict.at ? `, ${formatInstantWithZone(d.conflict.at)})` : d.conflict.by ? ')' : ''}. {d.conflict.message}
                      <div className="row" style={{ marginTop: 4 }}>
                        <Button size="s" onClick={() => workspace.rebaseOnRemote(d.key)}>
                          Reload and re-apply mine
                        </Button>
                        <Button size="s" variant="danger" onClick={() => workspace.adoptRemoteVersion(d.key)}>
                          Overwrite theirs
                        </Button>
                        <Button size="s" variant="ghost" onClick={() => workspace.discardEntity(d.key)}>
                          Discard mine
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

export type { EntityKey };
